// Login Page — up to 3-step flow:
//  Step 1: enter email → click Continue → backend tells us mode
//    • mode === 'password'       → show password field on same page
//    • mode === 'sign-in-link'   → native 2FA link auto-sent → check-email page
//  Step 2 (password mode): enter password → Sign In →
//    • logged in, OR
//    • mode === 'otp' (provider-mediated 2FA) → show OTP field (step 3)
//  Step 3 (otp mode only): enter 6-digit code → logged in
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Input, notification } from 'antd';
import Lottie from 'lottie-react';
import useLottieData from '../../hooks/useLottieData';
import { ThemeForm, ThemeButton } from '../Styles/Theme';
import {
	loginApi,
	loginReset,
	verifyOtpApi,
	resendOtpApi,
} from '../../store/actions/loginAction';
import { setPermissions } from '../../store/actions/permissionsAction';
import { handleInputChange } from '../../utils/formFunctions';
import { STORAGE_KEYS } from '../../utils/constants';
import { checkModulePermissions, getFirstAvailableRoute, isLogin } from '../../utils/helpers';
import { LoginWrapper } from './LoginStyle';
import { ArrowLeftOutlined } from '@ant-design/icons';

const Login = (props) => {
	const { login, loginReset, loginState, setPermissions, verifyOtp, resendOtp } = props;
	const [form] = ThemeForm.useForm();
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState('email'); // 'email' | 'password' | 'otp'
	const [otpSessionToken, setOtpSessionToken] = useState(null);
	const [otpMaskedEmail, setOtpMaskedEmail] = useState(null);
	const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
	// If the Lottie animation fails to load / parse, fall back to the static PNG.
	const loginLottie = useLottieData('/lottie/login.json');
	const [lottieFailed, setLottieFailed] = useState(false);
	const navigate = useNavigate();

	// OTP countdown — tick every second while on the OTP step.
	useEffect(() => {
		if (step !== 'otp' || otpSecondsLeft <= 0) return undefined;
		const id = setInterval(() => {
			setOtpSecondsLeft((s) => Math.max(0, s - 1));
		}, 1000);
		return () => clearInterval(id);
	}, [step, otpSecondsLeft]);

	// If the user is already signed in, don't show the login form — send them to
	// the dashboard (or their first accessible route if they can't see the dashboard).
	useEffect(() => {
		if (isLogin()) {
			// Per-org login redirect: backend sets redirect_chat_id when the
			// user's org has chat_redirection_enabled. Take that path before
			// the normal landing-page fallback so the user lands directly in
			// their fresh "New Insights" chat.
			if (loginState.data?.redirect_chat_id) {
				navigate(`/insight-engine/${loginState.data.redirect_chat_id}`);
				return;
			}
			const dashboardPermission = checkModulePermissions('dashboard');
			if (dashboardPermission.authorized) {
				navigate('/', { replace: true });
			} else {
				const first = getFirstAvailableRoute();
				navigate(first ? first.path : '/', { replace: true });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// Step 1 response: backend tells us which mode
		if (loginState.apiState === 'mfa_required') {
			setLoading(false);
			if (loginState.mfaSessionToken) {
				localStorage.setItem('mfaSessionToken', loginState.mfaSessionToken);
			}
			if (loginState.maskedEmail) {
				localStorage.setItem('mfaMaskedEmail', loginState.maskedEmail);
			}
			if (loginState.mfaExpiryMinutes) {
				localStorage.setItem('mfaExpiryMinutes', String(loginState.mfaExpiryMinutes));
			}
			loginReset();
			navigate('/auth/sign-in/check');
			return;
		}

		if (loginState.apiState === 'password_required') {
			setLoading(false);
			setStep('password');
			loginReset();
			return;
		}

		if (loginState.apiState === 'otp_required') {
			setLoading(false);
			setOtpSessionToken(loginState.mfaSessionToken || null);
			setOtpMaskedEmail(loginState.maskedEmail || null);
			// Cognito's challenge Session expires after ~3 minutes; show a
			// countdown so the user knows when they need to request a fresh code.
			const minutes = loginState.mfaExpiryMinutes || 3;
			setOtpSecondsLeft(Math.max(60, minutes * 60));
			setStep('otp');
			loginReset();
			return;
		}

		// Provider migration / admin-reset flow: user logged in with temp
		// password → backend says "please set a real password". Stash the
		// session token + user and navigate to the same page used by the
		// invitation-accept flow.
		if (loginState.apiState === 'reset_password_required') {
			setLoading(false);
			const data = loginState.data || {};
			if (data.sessionToken) {
				localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, data.sessionToken);
			}
			if (data.user) {
				localStorage.setItem(STORAGE_KEYS.PENDING_USER, JSON.stringify(data.user));
			}
			loginReset();
			navigate('/auth/np2w8q');
			return;
		}

		// Full login (password flow step 2)
		if (loginState.apiState === 'success') {
			setLoading(false);
			if (loginState.data) {
				localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, loginState.data.accessToken);
				localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, loginState.data.refreshToken);
				localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(loginState.data.user));
				if (loginState.data.expiresAt) {
					localStorage.setItem('expiresAt', loginState.data.expiresAt);
				}
				if (loginState.data.permissions) {
					localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(loginState.data.permissions));
					setPermissions(loginState.data.permissions);
				}
				if (loginState.data.appVersion) {
					localStorage.setItem('app_version', loginState.data.appVersion);
				}
			}
			loginReset();
			// Per-org login redirect: backend sets redirect_chat_id when the
			// user's org has chat_redirection_enabled. Take that path before
			// the normal landing-page fallback so the user lands directly in
			// their fresh "New Insights" chat.
			if (loginState.data?.redirect_chat_id) {
				navigate(`/insight-engine/${loginState.data.redirect_chat_id}`);
				return;
			}
			const dashboardPermission = checkModulePermissions('dashboard');
			if (dashboardPermission.authorized) {
				navigate('/');
			} else {
				const firstAvailableRoute = getFirstAvailableRoute();
				navigate(firstAvailableRoute ? firstAvailableRoute.path : '/');
			}
		} else if (loginState.apiState === 'error') {
			setLoading(false);
			const errorCode = loginState.data?.data?.code;
			if (errorCode === 'ACCOUNT_PENDING') {
				notification.warning({
					message: 'Please use the activation link in your invitation email.',
				});
			} else if (errorCode === 'ACCOUNT_SUSPENDED') {
				notification.error({
					message: 'Account suspended. Contact your administrator.',
				});
			} else {
				notification.error({ message: loginState.message || 'Login failed' });
			}
			loginReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginState]);

	const handleSubmit = () => {
		setLoading(true);
		if (step === 'email') {
			// Step 1: send only email
			login({ email: formData.email });
		} else if (step === 'password') {
			// Step 2: send email + password
			login({ email: formData.email, password: formData.password });
		} else if (step === 'otp') {
			// Step 3 (provider-mediated MFA): send email + otp + session token
			verifyOtp({ email: formData.email, mfaSessionToken: otpSessionToken, otp: formData.otp });
		}
	};

	const handleBackToEmail = () => {
		setStep('email');
		form.setFieldsValue({ password: '', otp: '' });
		setFormData({ ...formData, password: '', otp: '' });
		setOtpSessionToken(null);
		setOtpMaskedEmail(null);
		setOtpSecondsLeft(0);
	};

	const handleResendOtp = async () => {
		if (!otpSessionToken) return;
		const res = await resendOtp({ email: formData.email, mfaSessionToken: otpSessionToken });
		if (res?.status === 1) {
			if (res?.data?.mfaSessionToken) setOtpSessionToken(res.data.mfaSessionToken);
			notification.success({ message: 'Verification code resent' });
		} else {
			notification.error({ message: res?.message || 'Could not resend code' });
		}
	};

	return (
		<LoginWrapper>
			<Row gutter={[60, 16]}>
				<Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 10, order: 1 }} xl={{ span: 10, order: 1 }}>
					<div className='loginLogo'>
						<img src='/images/crowdvox-logo-purple.svg' alt='CrowdVox' />
					</div>
					<div className='loginContainer'>
						<h1>Sign in to CrowdVox</h1>
						{step === 'email' && (
							<p>Enter your email to continue.</p>
						)}
						{step === 'password' && (
							<p>
								Welcome back{' '}
								<span className='emailLinkGroup'>
									<strong>{formData.email}</strong>.{' '}
									<a onClick={handleBackToEmail}>
										<ArrowLeftOutlined style={{ fontSize: 11 }} /> Change email
									</a>
								</span>
							</p>
						)}
						{step === 'otp' && (
							<p>
								We've sent a 6-digit verification code to{' '}
								<span className='emailLinkGroup'>
									<strong>{otpMaskedEmail || formData.email}</strong>.{' '}
									<a onClick={handleBackToEmail}>
										<ArrowLeftOutlined style={{ fontSize: 11 }} /> Start over
									</a>
								</span>
								{otpSecondsLeft > 0 && (
									<>
										{' '}· <span style={{ color: otpSecondsLeft < 30 ? '#D4380D' : '#6D6A67' }}>
											Code valid for {Math.floor(otpSecondsLeft / 60)}:{String(otpSecondsLeft % 60).padStart(2, '0')}
										</span>
									</>
								)}
								{otpSecondsLeft === 0 && (
									<>
										{' '}· <span style={{ color: '#D4380D' }}>Code expired - please start over</span>
									</>
								)}
							</p>
						)}
						<ThemeForm
							form={form}
							name='loginForm'
							layout='vertical'
							autoComplete='off'
							onFinish={handleSubmit}
							className='loginFormV2'
						>
							<ThemeForm.Item
								name='email'
								label='Email'
								rules={[
									{ required: true, message: 'Please enter email address' },
									{ type: 'email', message: 'Please enter a valid email' },
								]}
							>
								<Input
									size='large'
									name='email'
									placeholder='Enter your email address'
									disabled={step === 'password' || step === 'otp'}
									onChange={(e) => handleInputChange(e, formData, setFormData)}
								/>
							</ThemeForm.Item>

							{step === 'password' && (
								<ThemeForm.Item
									name='password'
									label='Password'
									rules={[{ required: true, message: 'Please enter password' }]}
								>
									<Input.Password
										name='password'
										size='large'
										placeholder='Enter your password'
										autoFocus
										onChange={(e) => handleInputChange(e, formData, setFormData)}
									/>
								</ThemeForm.Item>
							)}

							{step === 'otp' && (
								<ThemeForm.Item
									name='otp'
									label='Verification Code'
									rules={[
										{ required: true, message: 'Please enter the 6-digit code' },
										{ pattern: /^\d{6}$/, message: 'Code must be 6 digits' },
									]}
								>
									<Input
										name='otp'
										size='large'
										placeholder='6-digit code'
										maxLength={6}
										autoFocus
										onChange={(e) => handleInputChange(e, formData, setFormData)}
									/>
								</ThemeForm.Item>
							)}

							{step === 'otp' && (
								<div style={{ marginTop: -8, marginBottom: 16, fontSize: 14 }}>
									<a onClick={handleResendOtp} style={{ color: '#2E45DA', cursor: 'pointer', fontWeight: 500 }}>
										Resend code
									</a>
								</div>
							)}

							<ThemeForm.Item style={{ marginBottom: 0, marginTop: 8 }}>
								{loading ? (
									<ThemeButton size='large' loading className='loginBtn'>
										{step === 'email' ? 'Checking...' : step === 'otp' ? 'Verifying...' : 'Signing In...'}
									</ThemeButton>
								) : (
									<ThemeButton size='large' className='loginBtn' htmlType='submit'>
										{step === 'email' ? 'Continue' : step === 'otp' ? 'Verify' : 'Sign in'}
									</ThemeButton>
								)}
							</ThemeForm.Item>
						</ThemeForm>
					</div>
				</Col>
				<Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 12, order: 1 }} lg={{ span: 14, order: 1 }} xl={{ span: 14, order: 1 }}>
					<div className='loginBannerCircle'>
						<div className='circle'>
							{lottieFailed || !loginLottie ? (
								<img src='/images/login-circle.png' alt='' />
							) : (
								<Lottie
									animationData={loginLottie}
									loop
									autoplay
									style={{ width: '100%', height: '100%' }}
									onError={() => setLottieFailed(true)}
								/>
							)}
						</div>
					</div>
				</Col>
			</Row>
		</LoginWrapper>
	);
};

const mapStateToProps = (state) => ({
	loginState: state.login,
});

const mapDispatchToProps = (dispatch) => ({
	login: (params) => dispatch(loginApi(params)),
	loginReset: () => dispatch(loginReset()),
	setPermissions: (params) => dispatch(setPermissions(params)),
	verifyOtp: (params) => dispatch(verifyOtpApi(params)),
	resendOtp: (params) => dispatch(resendOtpApi(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
